import { generatePath, type PathParam } from "react-router";

const generateParameterizedPath = generatePath as (
  pattern: string,
  parameters: Readonly<Record<string, string>>,
) => string;

type RouteParameters<Path extends string> = {
  readonly [Key in PathParam<Path>]: string | number;
};

type RawRouteParameters<Path extends string> = {
  readonly [Key in PathParam<Path>]: string;
};

export interface RouteParameterCodec<Value> {
  parse(value: string): Value;
  stringify(value: Value): string;
}

export class RouteParameterParseError extends Error {}

declare const routeHrefBrand: unique symbol;

export type RouteHref = string & { readonly [routeHrefBrand]: true };

type RouteParameterCodecs<Path extends string> = {
  readonly [Key in PathParam<Path>]: RouteParameterCodec<unknown>;
};

type ParsedRouteParameters<Codecs extends RouteParameterCodecs<string>> = {
  readonly [Key in keyof Codecs]: Codecs[Key] extends RouteParameterCodec<
    infer Value
  >
    ? Value
    : never;
};

type MergeRouteParameters<
  Parent extends Readonly<Record<string, unknown>>,
  Child extends Readonly<Record<string, unknown>>,
> = Readonly<{
  [Key in keyof Parent | keyof Child]: Key extends keyof Child
    ? Child[Key]
    : Key extends keyof Parent
      ? Parent[Key]
      : never;
}>;

export type ParameterizedRoute<
  Path extends string = string,
  Params extends Readonly<Record<string, unknown>> = RawRouteParameters<Path>,
  InputParams extends Readonly<Record<string, unknown>> = RouteParameters<Path>,
> = Readonly<{
  pattern: Path;
  href: (parameters: InputParams) => RouteHref;
  parseParams: (parameters: RawRouteParameters<Path>) => Params;
}>;

function routeParameterKeys<Path extends string>(
  pattern: Path,
): PathParam<Path>[] {
  return pattern.split("/").flatMap((segment) => {
    if (segment === "*") return ["*" as PathParam<Path>];
    if (!segment.startsWith(":")) return [];

    return [segment.replace(/^:/, "").replace(/\?$/, "") as PathParam<Path>];
  });
}

function createRoute<const Path extends string>(
  pattern: Path,
  codecs?: RouteParameterCodecs<Path>,
): ParameterizedRoute<
  Path,
  Readonly<Record<string, unknown>>,
  Readonly<Record<string, unknown>>
> {
  const parameterKeys = routeParameterKeys(pattern);

  return {
    pattern,
    parseParams(parameters) {
      return Object.fromEntries(
        parameterKeys.map((key) => [
          key,
          codecs ? codecs[key].parse(parameters[key]) : parameters[key],
        ]),
      );
    },
    href(parameters) {
      const stringParameters = Object.fromEntries(
        parameterKeys.map((key) => [
          key,
          codecs
            ? codecs[key].stringify(parameters[key])
            : String(parameters[key]),
        ]),
      ) as Readonly<Record<string, string>>;

      return generateParameterizedPath(pattern, stringParameters) as RouteHref;
    },
  };
}

export function defineRoute<const Path extends string>(
  pattern: Path,
): ParameterizedRoute<Path>;
export function defineRoute<
  const Path extends string,
  const Codecs extends RouteParameterCodecs<Path>,
>(
  pattern: Path,
  options: Readonly<{ params: Codecs }>,
): ParameterizedRoute<
  Path,
  ParsedRouteParameters<Codecs>,
  ParsedRouteParameters<Codecs>
>;
export function defineRoute<const Path extends string>(
  pattern: Path,
  options?: Readonly<{ params: RouteParameterCodecs<Path> }>,
): ParameterizedRoute<Path, Readonly<Record<string, unknown>>> {
  return createRoute(pattern, options?.params);
}

export function defineChildRoute<
  const ParentPath extends string,
  ParentParams extends Readonly<Record<string, unknown>>,
  ParentInputParams extends Readonly<Record<string, unknown>>,
  const ChildPath extends `/${string}`,
>(
  parent: ParameterizedRoute<ParentPath, ParentParams, ParentInputParams>,
  path: ChildPath,
): ParameterizedRoute<
  `${ParentPath}${ChildPath}`,
  MergeRouteParameters<ParentParams, RawRouteParameters<ChildPath>>,
  MergeRouteParameters<ParentInputParams, RouteParameters<ChildPath>>
>;
export function defineChildRoute<
  const ParentPath extends string,
  ParentParams extends Readonly<Record<string, unknown>>,
  ParentInputParams extends Readonly<Record<string, unknown>>,
  const ChildPath extends `/${string}`,
  const ChildCodecs extends RouteParameterCodecs<ChildPath>,
>(
  parent: ParameterizedRoute<ParentPath, ParentParams, ParentInputParams>,
  path: ChildPath,
  options: Readonly<{ params: ChildCodecs }>,
): ParameterizedRoute<
  `${ParentPath}${ChildPath}`,
  MergeRouteParameters<ParentParams, ParsedRouteParameters<ChildCodecs>>,
  MergeRouteParameters<ParentInputParams, ParsedRouteParameters<ChildCodecs>>
>;
export function defineChildRoute<
  const ParentPath extends string,
  ParentParams extends Readonly<Record<string, unknown>>,
  ParentInputParams extends Readonly<Record<string, unknown>>,
  const ChildPath extends `/${string}`,
>(
  parent: ParameterizedRoute<ParentPath, ParentParams, ParentInputParams>,
  path: ChildPath,
  options?: Readonly<{ params: RouteParameterCodecs<ChildPath> }>,
): ParameterizedRoute<
  `${ParentPath}${ChildPath}`,
  Readonly<Record<string, unknown>>,
  Readonly<Record<string, unknown>>
> {
  const child = createRoute(path, options?.params);

  return {
    pattern: `${parent.pattern}${path}`,
    parseParams(parameters) {
      return {
        ...parent.parseParams(
          parameters as unknown as RawRouteParameters<ParentPath>,
        ),
        ...child.parseParams(
          parameters as unknown as RawRouteParameters<ChildPath>,
        ),
      };
    },
    href(parameters) {
      return `${parent.href(parameters as ParentInputParams)}${child.href(parameters)}` as RouteHref;
    },
  };
}

export const integerRouteParameter: RouteParameterCodec<number> = {
  parse(value) {
    if (!/^\d+$/.test(value)) {
      throw new RouteParameterParseError(
        `Route parameter value ${value} is not an integer.`,
      );
    }

    return Number(value);
  },
  stringify: String,
};

export const LANDING = "/";
export const RETAILER_HOME = "/home";
export const SUPPLIER_ONBOARDING = "/supplier-onboarding";
export const CUSTOMERS = "/customers";
export const CUSTOMER = defineRoute(`${CUSTOMERS}/:connectionId`, {
  params: { connectionId: integerRouteParameter },
});
export const CUSTOMER_CONVERSATION = defineChildRoute(
  CUSTOMER,
  "/conversation",
);
export const CUSTOMER_ORDER = defineChildRoute(CUSTOMER, "/orders/:orderId", {
  params: {
    orderId: integerRouteParameter,
  },
});
export const SUPPLIERS = "/suppliers";
export const SUPPLIER = defineRoute(`${SUPPLIERS}/:supplierIdOrUrlId`);
export const ORDERS = "/orders";
//export const ORDER = `${CUSTOMER}/order`;
export const ORDER_SUPPLIER = defineRoute(`${CUSTOMER.pattern}/order`);
export const ORDER_BUYER = defineRoute(`${SUPPLIER.pattern}/order`);
export const CONNECTION_MESSAGE_LIST = `/messages`; // deprecated route now, use DIRECT_MESSAGE_LIST instead
export const DIRECT_MESSAGE_LIST = `/direct-messages`;
export const DIRECT_MESSAGE = defineRoute(
  `${DIRECT_MESSAGE_LIST}/:relationshipId`,
);
export const ORDER_COMMENT_LIST = `/order-comments`;
export const ORDER_COMMENT = defineRoute(`${ORDER_COMMENT_LIST}/:orderId`);
export const NOTIFICATIONS = "/notifications";
export const BUYERS = "/buyers";
export const BUYER = defineRoute(`${BUYERS}/:buyerId`, {
  params: { buyerId: integerRouteParameter },
});

// Keep these route names at the URL boundary so existing retailer links can
// be redirected to the buyer routes without reintroducing retailer terminology
// into the current navigation model.
export const LEGACY_RETAILERS = "/retailers";
export const LEGACY_RETAILER = defineRoute(`${LEGACY_RETAILERS}/:retailerId`);
export const CATEGORIES_BUYER = defineRoute(
  `${ORDER_BUYER.pattern}/categories`,
);
export const CATEGORY_BUYER = defineRoute(
  `${CATEGORIES_BUYER.pattern}/:categoryId`,
);
export const CHECKOUT_BUYER = defineRoute(`${ORDER_BUYER.pattern}/checkout`);
export const CHECKOUT_BUYER_ADD_ON = defineRoute(
  `${CHECKOUT_BUYER.pattern}/add-on`,
);
export const CHECKOUT_EDIT_ORDER_BUYER = defineRoute(
  `${ORDER_BUYER.pattern}/:orderId/checkout`,
);
export const CHECKOUT_DELIVERY_BUYER = defineRoute(
  `${CHECKOUT_BUYER.pattern}/delivery`,
);
export const CHECKOUT_REVIEW_BUYER = defineRoute(
  `${CHECKOUT_BUYER.pattern}/review`,
);
export const CHECKOUT_CONFIRM_BUYER = defineRoute(
  `${CHECKOUT_BUYER.pattern}/confirm`,
);
export const ORDER_SUCCESS_BUYER = defineRoute(
  `${ORDER_BUYER.pattern}/:orderId/success`,
);
export const CONFIRMATION = "/confirmation";
export const RYFT_PAYMENT_SESSION_REDIRECT = defineRoute(
  "/ryft/payment-sessions/redirect",
);
export const ORDER_DETAILS = defineRoute(`${ORDERS}/:orderId`);
export const CREATE_CONSIGNMENT = defineRoute(`/consignment/:orderId`);
export const ORDER_CONFIRMATION = defineRoute(
  `${ORDER_DETAILS.pattern}/confirmation`,
);
export const ORDER_INVOICE = defineRoute(`${ORDER_DETAILS.pattern}/invoice`);
export const ORDER_DELIVERY_NOTE = defineRoute(
  `${ORDER_DETAILS.pattern}/delivery-note`,
);
export const ORDER_EDIT = defineRoute(`${ORDER_DETAILS.pattern}/edit`);
export const NEW_SUPPLIER = `${SUPPLIERS}/new`;
export const ACCOUNT = `/account`;
export const ACCOUNT_SETTINGS = `${ACCOUNT}/settings`;
export const ACCOUNT_NOTIFICATION_SETTINGS = `${ACCOUNT_SETTINGS}/notifications`;
export const ACCOUNT_MANAGE_COMPANY_SETTINGS = `${ACCOUNT_SETTINGS}/manage-company`;
export const BUSINESS = `/business`;
export const BUSINESS_SETTINGS = `${BUSINESS}/settings`;
export const BUSINESS_EDIT_SETTINGS = `${BUSINESS}/settings/edit`;
export const BUSINESS_INTEGRATION_SETTINGS = `${BUSINESS_SETTINGS}/integrations`;
export const BUSINESS_INTEGRATION_CONFIRM_SETTINGS = `${BUSINESS_INTEGRATION_SETTINGS}/confirm`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DASHBOARD = `${BUSINESS_INTEGRATION_SETTINGS}/ryft`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ACCOUNTS = `${BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DASHBOARD}/accounts`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DOCUMENTS = `${BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DASHBOARD}/documents`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_BALANCE = `${BUSINESS_INTEGRATION_RYFT_NON_HOSTED_DASHBOARD}/balance`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING = `${BUSINESS_INTEGRATION_SETTINGS}/ryft/onboarding`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS = `${BUSINESS_INTEGRATION_SETTINGS}/ryft/onboarding/business`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS_DIRECTOR = `${BUSINESS_INTEGRATION_SETTINGS}/ryft/onboarding/business-director`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS_BANK_ACCOUNT = `${BUSINESS_INTEGRATION_SETTINGS}/ryft/onboarding/business-bank-account`;
export const BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_TERMS = `${BUSINESS_INTEGRATION_SETTINGS}/ryft/onboarding/terms`;
export const BUSINESS_OPENING_ORDER_FEES_SETTINGS = `${BUSINESS_SETTINGS}/opening-order-fees`;
export const BUSINESS_INVOICES_SETTINGS = `${BUSINESS_SETTINGS}/invoices`;
export const BUSINESS_PAYMENT_METHOD_SETTINGS = `${BUSINESS_SETTINGS}/payment-methods`;
export const BUSINESS_FEATURE_FLAG_SETTINGS = `${BUSINESS_SETTINGS}/feature-flags`;
export const BUSINESS_CONFIGURE_STORE_SETTINGS = `${BUSINESS_SETTINGS}/configure-store`;
export const BUSINESS_TERMS_AND_CONDITIONS = `${BUSINESS_SETTINGS}/terms-and-conditions`;
export const BUSINESS_FINANCE = `${BUSINESS_SETTINGS}/finance`;
export const BUSINESS_FINANCE_INVOICES = `${BUSINESS_FINANCE}/invoices`;
export const BUSINESS_FINANCE_OOF = `${BUSINESS_FINANCE}/opening-order-fees`;
export const BUSINESS_FINANCE_SELLAR_IMPORTS = `${BUSINESS_FINANCE}/sellar-imports`;
export const BUSINESS_FINANCE_CREDIT = `${BUSINESS_FINANCE}/sellar-credit`;
export const BUSINESS_FINANCE_CREDIT_PAYOUT = `${BUSINESS_FINANCE_CREDIT}/payout`;
export const EDIT_PROFILE = `/profile/edit`;
export const LOGIN = `/login`;
export const SIGNUP = `/signup`;
export const SIGNUP_BUSINESS = `${SIGNUP}/business`;
export const FORGOT_PASSWORD = `/forgot-password`;
export const SUPPLIER_LIST = `/supplier-list`;
export const JWT = `/jwt`;
export const ORDER_SUMMARY = "/order-summary";
export const ORDER_SUMMARY_DELIVERY_PREFERENCES =
  "/order-summary/delivery-preferences";
export const ORDER_SUMMARY_EDIT_ADD_SELECTION =
  "/order-summary/edit-add-selection";
export const OAUTH_LOGIN = `${LOGIN}/oauth2`;
export const PRODUCT_LIST = `/product-list`;
export const STOCK_OVERVIEW = `/stock-overview`;
export const STOCK_OVERVIEW_PRODUCT_DETAILS = defineRoute(
  `${STOCK_OVERVIEW}/:productId`,
);
export const NEW_PRODUCT = `${PRODUCT_LIST}/products/new`;
export const PRODUCT_LIST_CATEGORY = defineRoute(
  `${PRODUCT_LIST}/products/:productId`,
  { params: { productId: integerRouteParameter } },
);
export const LAYOUT_GROUPING = `/layout-grouping`;
export const LAYOUT_GROUPING_GROUP = defineRoute(`${LAYOUT_GROUPING}/:groupId`);
export const EDIT_PRODUCT = defineRoute(
  `${PRODUCT_LIST_CATEGORY.pattern}/edit`,
  { params: { productId: integerRouteParameter } },
);
export const NEW_BUYER = `${BUYERS}/new`;
export const NEW_VENUE = `/venues/new`;
export const PRICE_TIERS = `/price-tiers`;
export const PRICE_TIER = defineRoute(`${PRICE_TIERS}/:priceTierId`);
export const PRICE_TIER_EDIT = defineRoute(`${PRICE_TIER.pattern}/edit`);
export const AVAILABILITY_TIERS = `/product-catalogues`;
export const AVAILABILITY_TIER = defineRoute(
  `${AVAILABILITY_TIERS}/:availabilityTierId`,
  { params: { availabilityTierId: integerRouteParameter } },
);
export const AVAILABILITY_TIER_NEW = `${AVAILABILITY_TIERS}/new`;
export const ORDER_RULES = `/order-rules`;
export const ORDER_RULE = defineRoute(`${ORDER_RULES}/:orderRulesId`);
export const PRICE_LISTS = `/price-lists`;
export const PRICE_LIST = defineRoute(`${PRICE_LISTS}/:priceListId`);
export const VOLUME_DISCOUNTS = `${PRICE_LISTS}/volume-discounts`;
export const VOLUME_DISCOUNT = defineRoute(
  `${VOLUME_DISCOUNTS}/:volumeDiscountId`,
);
export const PROMOTIONS = `${PRICE_LISTS}/promotions`;
export const PROMOTION = defineRoute(`${PROMOTIONS}/:promotionId`, {
  params: { promotionId: integerRouteParameter },
});
export const PROMOTIONS_STANDALONE = `/promotions`;
export const PROMOTION_STANDALONE = defineRoute(
  `${PROMOTIONS_STANDALONE}/:promotionId`,
  { params: { promotionId: integerRouteParameter } },
);
export const DELIVERIES = `/deliveries`;
export const COLLECTIONS = `/collections`;
export const COLLECTION_DETAILS = defineRoute(`${COLLECTIONS}/:collectionId`, {
  params: { collectionId: integerRouteParameter },
});
export const DISTRIBUTION = `/distribution`;
export const DISTRIBUTION_PALLET_SHIPMENTS = `/pallet-shipments`;
export const DISTRIBUTION_PALLET_SHIPMENT_DETAILS = defineRoute(
  `${DISTRIBUTION_PALLET_SHIPMENTS}/:palletShipmentId`,
);
export const DISTRIBUTION_DETAILS_PROCESSING = defineRoute(
  `${DISTRIBUTION}/processing/:taskDate`,
);
export const DISTRIBUTION_DETAILS_ALLOCATED = defineRoute(
  `${DISTRIBUTION}/allocated/:taskId`,
);
export const NEW_COLLECTION = `/collections/new`;
export const DELIVERY_CHARGES = `/delivery-tiers`;
export const DELIVERY_METHOD_CHARGES = defineRoute(
  `${DELIVERY_CHARGES}/:method`,
);
export const DELIVERY_SETTINGS = `/delivery-settings`;
export const DELIVERY_SETTINGS_PACKAGING = `${DELIVERY_SETTINGS}/packaging`;
export const DELIVERY_AREA = "/delivery-area";
export const NEW_DELIVERY_AREA = `${DELIVERY_AREA}/new`;
export const UPDATE_DELIVERY_AREA = defineRoute(
  `${DELIVERY_AREA}/:deliveryAreaId`,
  { params: { deliveryAreaId: integerRouteParameter } },
);
export const ANALYTICS = "/analytics";
export const BUYER_SIGNUP = `${NEW_BUYER}/signup`;
export const PAYMENTS = `/payments`;
export const DISCOVER = "/discover";

export const DISCOVER_SEARCH = `${DISCOVER}/search`;
export const DISCOVER_SUPPLIER_MAP = `${DISCOVER}/supplier-map`;
export const DISCOVER_RECENTLY_ADDED = `${DISCOVER}/recently-added`;
export const DISCOVER_BEST_SELLERS = `${DISCOVER}/best-sellers`;
export const DISCOVER_SUPPLIERS_DIRECT = `${DISCOVER}/suppliers-direct`;
export const DISCOVER_SUPPLIERS_RECENT = `${DISCOVER}/suppliers-recently-added`;
export const DISCOVER_REGIONAL_SPOTLIGHT = `${DISCOVER}/regional`;
export const DISCOVER_PRODUCT_SPOTLIGHT = `${DISCOVER}/product-style`;
export const DISCOVER_PRE_ORDER = `${DISCOVER}/suppliers-pre-orders`;
export const DISCOVER_DISTRIBUTION = `${DISCOVER}/delivered-by-sellar`;
export const DRAFT_ORDERS = "/draft-orders";
export const FIREBASE_HANDLER = "/firebase-handler";
export const VERIFICATION_EMAIL_SENT = "/verification-email-sent";
export const STOREFRONT_SETTINGS = "/storefront-settings";
export const STOREFRONT_SHARE = "/storefront-share";
export const STOREFRONT_CONTENT = "/storefront-content";
export const STOREFRONT_LINKS = "/storefront-links";
export const STOREFRONT_LINKS_PREVIEW = "/storefront-links/preview";
export const EDUCATE_CONNECT = "/supplier-connect";
export const LCBF = "/london-craft-beer-festival-2024";
export const LOW_AND_NO = `${DISCOVER}/low-no-alcohol`;
export const PAYMENT_LINK = defineRoute("/payment-link/:orderId");
export const PAYMENT_LINK_REF = defineRoute("/payment-link/ref/:code");

export const SPOTLIGHTS = "/spotlight";
export const SPOTLIGHT = defineRoute(`${SPOTLIGHTS}/:spotlightId`);
export const COLLABAGEDDON = "/collabageddon";
export const COLLABAGEDDON_CHECKOUT = `${COLLABAGEDDON}/checkout`;
export const SELLAR_CREDIT_GET_STARTED = "/sellar-credit/get-started";

export const SELLAR_CREDIT_CREDIT_PROVIDER_SELECTION =
  "/sellar-credit/credit-provider-selection";
export const SELLAR_CREDIT_TAB = "/sellar-credit/tab";
export const SELLAR_CREDIT_STATEMENT = "/sellar-credit/statement";
export const SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY =
  "/sellar-credit/payment-allocation-history";
export const SELLAR_CREDIT_MANAGE_COMPANY = "/sellar-credit/manage-company";
export const SELLAR_CREDIT_DIRECT_DEBIT = `/sellar-credit/two-direct-debit/set-up`;
export const SELLAR_CREDIT_TWO_BUSINESS_REGISTRATION_APPLICATION = `/sellar-credit/two-business-registration-application`;

export const UNIFIED_CHECKOUT = "/checkout/unified";
export const UNIFIED_CHECKOUT_BASKET = defineRoute(
  `${UNIFIED_CHECKOUT}/:sdBasketUuid`,
);
export const UNIFIED_CHECKOUT_BASKET_OVERVIEW = defineRoute(
  `${UNIFIED_CHECKOUT_BASKET.pattern}/overview`,
);
export const UNIFIED_CHECKOUT_BASKET_DETAILS = defineRoute(
  `${UNIFIED_CHECKOUT_BASKET.pattern}/details`,
);
export const UNIFIED_CHECKOUT_BASKET_PAY = defineRoute(
  `${UNIFIED_CHECKOUT_BASKET.pattern}/pay`,
);
export const UNIFIED_CHECKOUT_BASKET_SUCCESS = defineRoute(
  `${UNIFIED_CHECKOUT_BASKET.pattern}/success`,
);

export const CUSTOMER_ZONES = "/customer-zones";
export const CUSTOMER_ZONE = defineRoute(`${CUSTOMER_ZONES}/:customerZoneId`);

export const DELIVERY = "/delivery";
export const DELIVERY_ZONES = `${DELIVERY}/zones`;
export const DELIVERY_ZONE = defineRoute(`${DELIVERY_ZONES}/:deliveryZoneId`);
export const DELIVERY_METHODS = `${DELIVERY}/methods`;
export const DELIVERY_METHOD = defineRoute(
  `${DELIVERY_METHODS}/:deliveryMethodId`,
);
export const DELIVERY_SETTINGS_V2 = `${DELIVERY}/settings`;
export const DELIVERY_SCHEDULES = `${DELIVERY}/schedules`;
export const DELIVERY_SCHEDULE_NEW = `${DELIVERY_SCHEDULES}/new`;
export const DELIVERY_SCHEDULE = defineRoute(
  `${DELIVERY_SCHEDULES}/:deliveryAreaId`,
  { params: { deliveryAreaId: integerRouteParameter } },
);

export const SUPPLIER_DISTRIBUTION = `${DELIVERY}/sellar-distribution`;
